import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { AdminPage, EmptyState, ErrorState, Panel, Skeleton } from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { errorMessage } from '@/lib/errors';
import { label, scope as scopeLabels } from '@/lib/i18n/he';

/**
 * תפקידים והרשאות (PRD 5.13): שורה לכל מפתח הרשאה, מקובצות לפי מודול,
 * עמודה לכל תפקיד, ותיבת סימון בכל תא. זה המקום שבו מחליטים "מי רואה מה"
 * תוך כדי עבודה, בלי לשנות קוד.
 */
type Permission = { key: string; module: string; label: string };
type Role = { id: string; name: string; scope: string; is_system: boolean };
type Grant = { role_id: string; permission_key: string };

const KEY = ['roles-matrix'] as const;

function useMatrix() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const [permissions, roles, grants] = await Promise.all([
        supabase.from('permissions').select('key, module, label').order('module').order('key'),
        supabase.from('roles').select('id, name, scope, is_system').order('name'),
        supabase.from('role_permissions').select('role_id, permission_key'),
      ]);

      if (permissions.error) throw permissions.error;
      if (roles.error) throw roles.error;
      if (grants.error) throw grants.error;

      return {
        permissions: (permissions.data ?? []) as Permission[],
        roles: (roles.data ?? []) as Role[],
        granted: new Set((grants.data ?? []).map((g: Grant) => `${g.role_id}|${g.permission_key}`)),
      };
    },
    staleTime: 60 * 1000,
  });
}

export function RolesPage() {
  const canEdit = usePerm('admin.roles');
  const { data, isPending, isError, refetch } = useMatrix();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const toggle = useMutation({
    mutationFn: async ({ roleId, key, next }: { roleId: string; key: string; next: boolean }) => {
      const { error: failure } = next
        ? await supabase.from('role_permissions').insert({ role_id: roleId, permission_key: key })
        : await supabase
            .from('role_permissions')
            .delete()
            .eq('role_id', roleId)
            .eq('permission_key', key);
      if (failure) throw failure;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
    onError: (caught) => setError(errorMessage(caught)),
  });

  if (isPending) {
    return (
      <AdminPage title="תפקידים והרשאות">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </AdminPage>
    );
  }

  if (isError) {
    return (
      <AdminPage title="תפקידים והרשאות">
        <ErrorState onRetry={() => void refetch()} />
      </AdminPage>
    );
  }

  const { permissions, roles, granted } = data;
  if (roles.length === 0)
    return <EmptyState title="אין תפקידים" description="צריך להגדיר תפקיד אחד לפחות." />;

  // מקבצים לפי מודול, בסדר שבו הם חוזרים מבסיס הנתונים
  const groups = permissions.reduce<Record<string, Permission[]>>((acc, permission) => {
    (acc[permission.module] ??= []).push(permission);
    return acc;
  }, {});

  return (
    <AdminPage
      title="תפקידים והרשאות"
      subtitle="סימון משנה מיד מה העובד רואה, בלי פריסה מחדש"
      width="full"
    >
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      <Panel bodyClassName="p-0">
        <div className="max-h-[75dvh] overflow-auto">
          <table className="w-full border-collapse text-body-sm">
            <caption className="sr-only">
              מטריצת הרשאות: שורה לכל מפתח הרשאה, עמודה לכל תפקיד
            </caption>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="sticky start-0 top-0 z-20 border-b border-line bg-cream p-2 text-start"
                >
                  הרשאה
                </th>
                {roles.map((role) => (
                  <th
                    key={role.id}
                    scope="col"
                    className="sticky top-0 z-10 border-b border-line bg-cream p-2 text-center align-bottom"
                  >
                    <span className="grid gap-0.5">
                      <span className="font-medium">{role.name}</span>
                      <span className="text-label font-normal text-muted">
                        {label(scopeLabels, role.scope)}
                      </span>
                    </span>
                  </th>
                ))}
              </tr>
            </thead>

            {Object.entries(groups).map(([moduleName, items]) => (
              <tbody key={moduleName}>
                <tr>
                  <th
                    scope="colgroup"
                    colSpan={roles.length + 1}
                    className="bg-cream p-2 text-start font-display text-title font-semibold"
                  >
                    {moduleName}
                  </th>
                </tr>
                {items.map((permission) => (
                  <tr key={permission.key} className="border-t border-line">
                    <th
                      scope="row"
                      className="sticky start-0 bg-surface p-2 text-start font-normal"
                    >
                      <span className="grid">
                        <span>{permission.label}</span>
                        <code className="text-label text-muted" dir="ltr">
                          {permission.key}
                        </code>
                      </span>
                    </th>
                    {roles.map((role) => {
                      const isOn = granted.has(`${role.id}|${permission.key}`);
                      return (
                        <td key={role.id} className="p-2 text-center">
                          <input
                            type="checkbox"
                            checked={isOn}
                            disabled={!canEdit || toggle.isPending}
                            aria-label={`${permission.label} לתפקיד ${role.name}`}
                            onChange={() => {
                              setError(null);
                              toggle.mutate({
                                roleId: role.id,
                                key: permission.key,
                                next: !isOn,
                              });
                            }}
                            className="size-5"
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      </Panel>

      {!canEdit && (
        <p className="m-0 text-body-sm text-muted">
          אפשר לצפות במטריצה. לעריכה צריך את ההרשאה "ניהול תפקידים והרשאות".
        </p>
      )}
    </AdminPage>
  );
}
